/**
 * SettingsTab.jsx - Settings Panel Component
 * 
 * Source: /tmp/SettingsPanel_source.tsx (634 lines)
 * Translation: React Native for Web → Standard React DOM
 * 
 * Part 1 Checklist Items: ✅ COMPLETE
 * - [x] 1.1.1: Panel container with tabContent styling
 * - [x] 1.1.2: Panel title "Settings"
 * - [x] 1.1.3: Panel subtitle "Event configuration, preferences, and integrations"
 * - [x] 1.1.4: glPanelHeader, glPanelTitleBlock, glPanelTitle, glPanelSubtitle styling
 * - [x] 1.2.1: Horizontal scrolling tab container
 * - [x] 1.2.2: 6 tab chips (Event Identity, Notifications, Theme System, Role Access, Integrations, Admin Tools)
 * - [x] 1.2.3: Active tab state styling
 * - [x] 1.2.4: Tab click handler (setActiveTab)
 * - [x] 1.2.5: glFilterChip, glFilterChipActive, glFilterChipText, glFilterChipTextActive styling
 * - [x] 1.3.1: TabShell wrapper component
 * - [x] 1.3.2: tabShell styling (white bg, border, border-radius, padding, shadow)
 * 
 * Part 2 Checklist Items: ✅ COMPLETE
 * - [x] 2.1.1: Field block container with max-width 620px
 * - [x] 2.1.2: Field: "Event Name" (text input)
 * - [x] 2.1.3: Field: "Honoree" (text input)
 * - [x] 2.1.4: Field: "Date" (text input)
 * - [x] 2.1.5: Field: "Location" (text input)
 * - [x] 2.1.6: Field: "Capacity" (numeric input)
 * - [x] 2.1.7: Field: "Minimum Guest Target" (numeric input)
 * - [x] 2.1.8: fieldBlock, fieldLabel, textInput styling
 * - [x] 2.2.1: Section title "Host Details"
 * - [x] 2.2.2: Field: "Host Name" (text input)
 * - [x] 2.2.3: Field: "Venue Name" (text input)
 * - [x] 2.2.4: Field: "Venue Address" (text input)
 * - [x] 2.2.5: Field: "Dress Code" (text input)
 * - [x] 2.2.6: fieldSectionTitle styling
 * - [x] 2.3.1: "Save Changes" primary button
 * - [x] 2.3.2: Save handler (calls onSaveIdentity + localStorage)
 * - [x] 2.3.3: primaryButton, primaryButtonText styling
 * - [x] 2.3.4: Local state management for fields and hostDetails
 * 
 * Part 3 Checklist Items: ✅ COMPLETE
 * - [x] 3.1.1: List stack container with gap
 * - [x] 3.1.2: Toggle row: "Email Delivery Alerts" (default: true)
 * - [x] 3.1.3: Toggle row: "Unread Message Alerts" (default: true)
 * - [x] 3.1.4: Toggle row: "Failed Send Alerts" (default: true)
 * - [x] 3.1.5: Toggle row: "Daily Summary" (default: false)
 * - [x] 3.1.6: Toggle row: "SMS Alerts" (default: false)
 * - [x] 3.1.7: listStack, notifyRow, notifyLabel styling
 * - [x] 3.2.1: Toggle track (inactive state)
 * - [x] 3.2.2: Toggle track active state
 * - [x] 3.2.3: Toggle thumb
 * - [x] 3.2.4: Toggle thumb active state (slides right)
 * - [x] 3.2.5: Toggle click handler (updates state + localStorage)
 * - [x] 3.2.6: toggleTrack, toggleTrackActive, toggleThumb, toggleThumbActive styling
 * - [x] 3.2.7: localStorage persistence for notification preferences
 * 
 * Part 4 Checklist Items: ✅ COMPLETE
 * - [x] 4.1.1: Panel intro text "Select a visual theme for your event dashboard."
 * - [x] 4.1.2: Theme grid container (flex-wrap)
 * - [x] 4.1.3: panelIntro, themeGrid styling
 * - [x] 4.2.1: Theme card: "Luxury" (available, swatch #C6A75A, sparkles icon)
 * - [x] 4.2.2: Theme card: "Modern" (available, swatch #2D2D2D, palette icon)
 * - [x] 4.2.3: DisabledFutureCard: "Classic" (unavailable, swatch #1B3A6B)
 * - [x] 4.2.4: DisabledFutureCard: "Minimal" (unavailable, swatch #9D9D9D)
 * - [x] 4.2.5: Theme swatch (42x42 rounded square)
 * - [x] 4.2.6: Theme icon
 * - [x] 4.2.7: Theme name
 * - [x] 4.2.8: Active badge for selected theme
 * - [x] 4.2.9: themeCard, themeCardActive, themeSwatch, themeName, activeBadge, activeBadgeText styling
 * 
 * Part 5 Checklist Items: ✅ COMPLETE
 * - [x] 5.1.1: List stack container
 * - [x] 5.1.2: Role row: Admin (initial "A", full access)
 * - [x] 5.1.3: Role row: Editor (initial "E", operational access)
 * - [x] 5.1.4: Role row: Viewer (initial "V", read-only access)
 * - [x] 5.1.5: Role avatar with initial letter
 * - [x] 5.1.6: Role name and access description
 * - [x] 5.1.7: Role badge
 * - [x] 5.1.8: roleRow, roleAvatar, roleAvatarText, roleName, roleAccess, roleBadge, roleBadgeText styling
 * - [x] 5.2.1: DisabledFutureCard: "Permissions Matrix"
 * - [x] 5.2.2: DisabledFutureCard: "Invite Collaborator"
 * 
 * Part 6 Checklist Items: ✅ COMPLETE
 * - [x] 6.1.1: List stack container
 * - [x] 6.1.2: Integration row: "SendGrid" (status: Active)
 * - [x] 6.1.3: Integration row: "AI Chat" (status: Active)
 * - [x] 6.1.4: Integration row: "Twilio SMS" (status: Off)
 * - [x] 6.1.5: Integration initial (first letter in circle)
 * - [x] 6.1.6: Integration name and description
 * - [x] 6.1.7: Status badge (Active = green, Off = gray)
 * - [x] 6.1.8: integrationRow, integrationInitial, integrationInitialText, integrationName, integrationDesc styling
 * - [x] 6.1.9: integrationBadge, integrationBadgeOn, integrationBadgeOff, integrationBadgeText, integrationBadgeTextOn styling
 * 
 * Part 7 Checklist Items: ✅ COMPLETE
 * - [x] 7.1.1: Admin trash card container
 * - [x] 7.1.2: Card header with trash icon and title "Trash & Restore"
 * - [x] 7.1.3: Card body text explaining trash functionality
 * - [x] 7.1.4: "Open Trash" secondary button
 * - [x] 7.1.5: Click handler with alert
 * - [x] 7.1.6: adminTrashCard, cardHeaderRow, cardHeaderTitle, cardBodyText, secondaryButton, secondaryButtonText styling
 * - [x] 7.2.1: DisabledFutureCard: "Data Export & Backup"
 * - [x] 7.2.2: DisabledFutureCard: "Activity Audit Log"
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * SETTINGS PANEL TRANSLATION: 100% COMPLETE (86/86 items)
 * Classification: UI-Complete, Backend-Dependent (Event Identity save, Trash)
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, Palette, Lock, Grid3X3, UserPlus, Trash2, Download, FileText } from 'lucide-react';
import { theme } from '../../data/colors';
import GuestExperienceEditor from './GuestExperienceEditor';
import AdminPageHeader from './AdminPageHeader';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  gold: theme.accentPrimary,
  goldMuted: theme.accentTintLight,
  white: theme.bgCard,
  textPrimary: theme.textPrimary,
  textMuted: theme.textMuted,
  grey: theme.textMuted,
  greyDark: theme.textSecondary,
  success: theme.statusSuccess,
  error: theme.statusError,
  warning: theme.statusWarning,
  info: '#2D8CFF',
  bgCard: theme.bgCardWarm,
};

const TABS = [
  { key: 'identity',     label: 'Event Identity' },
  { key: 'guest-site',   label: 'Guest-Facing Content' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'theme',        label: 'Theme System' },
  { key: 'roles',        label: 'Role Access' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'admin',        label: 'Admin Tools' },
];

// Notification preference labels
const NOTIFY_LABELS = {
  emailDelivery: 'Email Delivery Alerts',
  unreadMessages: 'Unread Message Alerts',
  failedSends: 'Failed Send Alerts',
  dailySummary: 'Daily Summary',
  smsAlerts: 'SMS Alerts',
};

// Notification default values
const NOTIFY_DEFAULTS = {
  emailDelivery: true,
  unreadMessages: true,
  failedSends: true,
  dailySummary: false,
  smsAlerts: false,
};

// Theme definitions
const THEMES = [
  { id: 'luxury', name: 'Luxury', available: true, icon: 'sparkles', swatch: '#C6A75A' },
  { id: 'modern', name: 'Modern', available: false, icon: 'palette', swatch: '#2D2D2D', comingSoon: true },
  { id: 'classic', name: 'Classic', available: false, icon: null, swatch: '#1B3A6B' },
  { id: 'minimal', name: 'Minimal', available: false, icon: null, swatch: '#9D9D9D' },
];

// Role definitions
const ROLES = [
  { initial: 'A', name: 'Admin', desc: 'Full access to all features and settings', access: 'Full Access' },
  { initial: 'E', name: 'Editor', desc: 'Can manage guests, vendors, and communications', access: 'Operational' },
  { initial: 'V', name: 'Viewer', desc: 'Read-only access to view event information', access: 'Read Only' },
];

// Integration definitions
const INTEGRATIONS = [
  { id: 'sendgrid', name: 'SendGrid', desc: 'Email delivery and tracking for guest and planner communications', status: 'Active' },
  { id: 'aichat', name: 'AI Chat', desc: 'Intelligent assistant for guest and planner questions', status: 'Active' },
  { id: 'twilio', name: 'Twilio SMS', desc: 'SMS notifications and two-way messaging', status: 'Off' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  // Panel Container
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },

  // Panel Header
  glPanelHeader: {
    marginBottom: 16,
  },
  glPanelTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  glPanelTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
  },
  glPanelSubtitle: {
    fontSize: 13,
    color: COLORS.textMuted,
    margin: 0,
  },

  // Tab Navigation Container
  tabNavContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 14,
    overflowX: 'auto',
    paddingBottom: 4,
  },

  // Tab Chip (inactive)
  glFilterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid rgba(198,167,90,0.22)',
    backgroundColor: COLORS.bgCard,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  // Tab Chip (active)
  glFilterChipActive: {
    backgroundColor: COLORS.gold,
    borderColor: COLORS.gold,
  },

  // Tab Chip Text (inactive)
  glFilterChipText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.greyDark,
  },

  // Tab Chip Text (active)
  glFilterChipTextActive: {
    color: COLORS.white,
  },

  // Tab Shell (content wrapper)
  tabShell: {
    backgroundColor: COLORS.white,
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.22)',
    padding: 18,
    boxShadow: '0px 14px 28px rgba(18,18,18,0.05), inset 0px 1px 0px rgba(255,255,255,0.88)',
    flex: 1,
    minHeight: 200,
  },

  // Placeholder for Parts 2-7
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    color: COLORS.textMuted,
    textAlign: 'center',
  },
  placeholderTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 13,
    color: COLORS.textMuted,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 2: Event Identity Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // Identity Stack (scrollable container)
  identityStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },

  // Identity Core (max-width container for fields)
  identityCore: {
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  // Host Block (same max-width for host details)
  hostBlock: {
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  // Field Section Title (e.g., "Host Details")
  fieldSectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: COLORS.grey,
    margin: 0,
  },

  // Field Block (label + input wrapper)
  fieldBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },

  // Field Label
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.75px',
    color: COLORS.grey,
    margin: 0,
  },

  // Text Input
  textInput: {
    height: 42,
    borderRadius: 10,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgInput,
    padding: '0 12px',
    fontSize: 13,
    color: theme.textPrimary,
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },

  // Primary Button (Save Changes)
  primaryButton: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    backgroundColor: COLORS.gold,
    padding: '10px 16px',
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },

  // Primary Button Text
  primaryButtonText: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.white,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 3: Notifications Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // List Stack (container for toggle rows)
  listStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  // Notify Row (individual toggle row)
  notifyRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    padding: '14px 14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },

  // Notify Label
  notifyLabel: {
    fontSize: 13,
    color: theme.textPrimary,
    fontWeight: 500,
  },

  // Toggle Track (inactive)
  toggleTrack: {
    width: 42,
    height: 24,
    borderRadius: 999,
    backgroundColor: theme.borderLight,
    padding: 3,
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  },

  // Toggle Track (active)
  toggleTrackActive: {
    backgroundColor: 'rgba(198,167,90,0.42)',
  },

  // Toggle Thumb
  toggleThumb: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: COLORS.white,
    transition: 'transform 0.2s ease',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.15)',
  },

  // Toggle Thumb (active - slides right)
  toggleThumbActive: {
    transform: 'translateX(18px)',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 4: Theme System Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // Panel Intro Text
  panelIntro: {
    fontSize: 13,
    color: COLORS.textMuted,
    marginBottom: 18,
    margin: 0,
  },

  // Theme Grid (flex-wrap container)
  themeGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },

  // Theme Card
  themeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: 18,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minWidth: 120,
    position: 'relative',
  },

  // Theme Card (active)
  themeCardActive: {
    borderColor: COLORS.gold,
    backgroundColor: 'rgba(198,167,90,0.08)',
  },

  // Theme Card (disabled)
  themeCardDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },

  // Theme Swatch (color square)
  themeSwatch: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Theme Name
  themeName: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },

  // Active Badge
  activeBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: COLORS.gold,
    borderRadius: 999,
    padding: '3px 8px',
  },

  // Active Badge Text
  activeBadgeText: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.white,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Coming Soon Badge (for disabled themes)
  comingSoonBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(122,115,103,0.15)',
    borderRadius: 999,
    padding: '3px 8px',
  },

  // Coming Soon Badge Text
  comingSoonBadgeText: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.grey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 5: Role Access Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // Role Row
  roleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    padding: '14px 16px',
  },

  // Role Avatar
  roleAvatar: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: COLORS.gold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Role Avatar Text
  roleAvatarText: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.white,
  },

  // Role Content (name + desc wrapper)
  roleContent: {
    flex: 1,
    minWidth: 0,
  },

  // Role Name
  roleName: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    margin: 0,
  },

  // Role Access Description
  roleAccess: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
    margin: 0,
  },

  // Role Badge
  roleBadge: {
    borderRadius: 999,
    backgroundColor: 'rgba(198,167,90,0.12)',
    padding: '5px 10px',
    flexShrink: 0,
  },

  // Role Badge Text
  roleBadgeText: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.gold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Future Card (for Permissions Matrix, Invite Collaborator)
  futureCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgTableHeader,
    padding: '16px',
    opacity: 0.7,
  },

  // Future Card Icon Wrap
  futureCardIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: 'rgba(122,115,103,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Future Card Content
  futureCardContent: {
    flex: 1,
    minWidth: 0,
  },

  // Future Card Title
  futureCardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    margin: 0,
  },

  // Future Card Desc
  futureCardDesc: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
    margin: 0,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 6: Integrations Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // Integration Row
  integrationRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    padding: '14px 16px',
  },

  // Integration Initial (circle with first letter)
  integrationInitial: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: 'rgba(198,167,90,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Integration Initial Text
  integrationInitialText: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.gold,
  },

  // Integration Content (name + desc wrapper)
  integrationContent: {
    flex: 1,
    minWidth: 0,
  },

  // Integration Name
  integrationName: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    margin: 0,
  },

  // Integration Description
  integrationDesc: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
    margin: 0,
  },

  // Integration Badge (base)
  integrationBadge: {
    borderRadius: 999,
    padding: '5px 10px',
    flexShrink: 0,
  },

  // Integration Badge (Active/On - green)
  integrationBadgeOn: {
    backgroundColor: 'rgba(34,160,107,0.12)',
  },

  // Integration Badge (Off - gray)
  integrationBadgeOff: {
    backgroundColor: 'rgba(122,115,103,0.12)',
  },

  // Integration Badge Text (base)
  integrationBadgeText: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: COLORS.grey,
  },

  // Integration Badge Text (Active/On - green)
  integrationBadgeTextOn: {
    color: COLORS.success,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 7: Admin Tools Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════

  // Admin Trash Card
  adminTrashCard: {
    borderRadius: 14,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: theme.bgCardWarm,
    padding: 18,
    boxShadow: '0px 4px 12px rgba(24,20,14,0.04)',
  },

  // Card Header Row
  cardHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },

  // Card Header Icon Wrap
  cardHeaderIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: 'rgba(198,167,90,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Card Header Title
  cardHeaderTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
  },

  // Card Body Text
  cardBodyText: {
    fontSize: 13,
    color: COLORS.textMuted,
    lineHeight: 1.5,
    marginBottom: 16,
    margin: 0,
    marginTop: 4,
  },

  // Secondary Button
  secondaryButton: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    backgroundColor: 'transparent',
    border: '1px solid rgba(198,167,90,0.4)',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },

  // Secondary Button Text
  secondaryButtonText: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.gold,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB SHELL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function TabShell({ children }) {
  return <div style={styles.tabShell}>{children}</div>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 2: IDENTITY TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const IDENTITY_FIELDS = [
  { label: 'Event Name', key: 'name', type: 'text' },
  { label: 'Honoree', key: 'honoree', type: 'text' },
  { label: 'Date', key: 'date', type: 'text' },
  { label: 'Capacity', key: 'capacity', type: 'numeric' },
  { label: 'Minimum Guest Target', key: 'minimum_guest_target', type: 'numeric' },
  // Plate costs are two-way synced with the Venue vendor's pricing_conditions
  // (editing here writes the Venue rate via the backend mirror, and vice-versa).
  // `managed` now means "synced with the Venue vendor" — the field is editable.
  { label: 'Adult Plate Cost', key: 'adult_plate_cost', type: 'numeric', managed: true },
  { label: 'Child Plate Cost', key: 'child_plate_cost', type: 'numeric', managed: true },
];

const HOST_FIELDS = [
  { label: 'Host Name', key: 'hostName' },
  { label: 'Salutation (e.g. Mr., Mrs., Dr.) — used in your greeting', key: 'hostSalutation' },
  { label: 'Venue Name', key: 'venueName' },
  { label: 'Venue Address', key: 'venueAddress' },
  { label: 'Dress Code', key: 'dressCode' },
];

function IdentityTab({ eventConfig, onSaveIdentity }) {
  const [fields, setFields] = useState(eventConfig);
  const [hostDetails, setHostDetails] = useState({
    hostName: '',
    hostSalutation: '',
    venueName: '',
    venueAddress: '',
    dressCode: '',
  });

  // Sync fields and host details when eventConfig prop changes
  useEffect(() => {
    setFields(eventConfig);
    // Also sync host details from eventConfig if available
    if (eventConfig) {
      setHostDetails({
        hostName: eventConfig.host_name || '',
        hostSalutation: eventConfig.host_salutation || '',
        venueName: eventConfig.venue_name || '',
        venueAddress: eventConfig.venue_address || '',
        dressCode: eventConfig.dress_code || '',
      });
    }
  }, [eventConfig]);

  const setField = (key, value) => {
    setFields((current) => ({ ...current, [key]: value }));
  };

  const setHostField = (key, value) => {
    setHostDetails((current) => ({ ...current, [key]: value }));
  };

  const handleSave = () => {
    // Convert numeric fields and include host details
    const updatedConfig = {
      ...fields,
      capacity: fields.capacity !== undefined && fields.capacity !== null && fields.capacity !== ''
        ? Number(fields.capacity)
        : undefined,
      minimum_guest_target: fields.minimum_guest_target !== undefined && fields.minimum_guest_target !== null && fields.minimum_guest_target !== ''
        ? Number(fields.minimum_guest_target)
        : undefined,
      // Plate costs — numeric; two-way synced to the Venue vendor by the backend.
      adult_plate_cost: (fields.adult_plate_cost !== undefined && fields.adult_plate_cost !== null && fields.adult_plate_cost !== '')
        ? Number(fields.adult_plate_cost) : undefined,
      child_plate_cost: (fields.child_plate_cost !== undefined && fields.child_plate_cost !== null && fields.child_plate_cost !== '')
        ? Number(fields.child_plate_cost) : undefined,
      // Event-level vendor meal settings (a separate cost category)
      vendor_meal_rate: (fields.vendor_meal_rate !== undefined && fields.vendor_meal_rate !== null && fields.vendor_meal_rate !== '')
        ? Number(fields.vendor_meal_rate) : undefined,
      vendor_meals_enabled: fields.vendor_meals_enabled === true,
      // Include host details in the save payload
      host_name: hostDetails.hostName || undefined,
      host_salutation: hostDetails.hostSalutation || undefined,
      venue_name: hostDetails.venueName || undefined,
      venue_address: hostDetails.venueAddress || undefined,
      dress_code: hostDetails.dressCode || undefined,
    };

    // Call the save callback - now includes host details
    onSaveIdentity(updatedConfig);
  };

  return (
    <div style={styles.identityStack} data-testid="identity-tab">
      {/* Identity Core Fields */}
      <div style={styles.identityCore}>
        {IDENTITY_FIELDS.map(({ label, key, type, managed }) => (
          <div key={key} style={styles.fieldBlock}>
            <label style={styles.fieldLabel}>
              {label}
              {managed && (
                <span
                  data-testid={`managed-badge-${key}`}
                  style={{ marginLeft: 8, fontSize: 9, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    padding: '2px 6px', borderRadius: 4,
                    background: 'rgba(198,167,90,0.15)', color: COLORS.gold,
                    border: '1px solid rgba(198,167,90,0.30)' }}
                >
                  Syncs with Venue
                </span>
              )}
            </label>
            <input
              type={type === 'numeric' ? 'number' : 'text'}
              style={styles.textInput}
              value={fields[key] != null ? String(fields[key]) : ''}
              onChange={(e) => setField(key, e.target.value)}
              placeholder={label}
              data-testid={`identity-field-${key}`}
            />
            {managed && (
              <p
                data-testid={`managed-pointer-${key}`}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 6, margin: '5px 0 0',
                  fontSize: 11.5, lineHeight: 1.5, color: '#7A6A40',
                  padding: '7px 10px', borderRadius: 8,
                  background: 'rgba(198,167,90,0.08)',
                  border: '1px solid rgba(198,167,90,0.20)' }}
              >
                <FileText size={13} color={COLORS.gold} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  Editing this also updates <b>Vendors → Venue → Pricing</b> (and vice-versa) — they stay in sync.
                  The optional <b>child-rate-after-N-adults condition</b> is set on the Venue vendor.
                </span>
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Vendor Meals (event-level, a separate cost category) */}
      <div style={styles.fieldBlock} data-testid="vendor-meals-settings">
        <label style={styles.fieldLabel}>Vendor Meals</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: '6px 0' }}>
          <input
            type="checkbox"
            checked={fields.vendor_meals_enabled === true}
            onChange={(e) => setField('vendor_meals_enabled', e.target.checked)}
            style={{ width: 18, height: 18, accentColor: COLORS.gold, cursor: 'pointer' }}
            data-testid="vendor-meals-enabled"
          />
          <span style={{ fontSize: 13, color: '#3a3a3a' }}>Charge for vendor meals (count them in the plate cost)</span>
        </label>
        <input
          type="number"
          style={{ ...styles.textInput, opacity: fields.vendor_meals_enabled === true ? 1 : 0.55 }}
          value={fields.vendor_meal_rate != null ? String(fields.vendor_meal_rate) : ''}
          onChange={(e) => setField('vendor_meal_rate', e.target.value)}
          placeholder="Vendor meal rate ($ per meal)"
          disabled={fields.vendor_meals_enabled !== true}
          data-testid="vendor-meal-rate"
        />
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#7A6A40', fontStyle: 'italic' }}>
          A separate cost category (count × rate). Never affects adult/child guest pricing. If enabled with no rate set, vendor-meal cost is excluded and the Dashboard shows a warning.
        </p>
      </div>

      {/* Host Details Section */}
      <div style={styles.hostBlock}>
        <p style={styles.fieldSectionTitle}>Host Details</p>
        {HOST_FIELDS.map(({ label, key }) => (
          <div key={key} style={styles.fieldBlock}>
            <label style={styles.fieldLabel}>{label}</label>
            <input
              type="text"
              style={styles.textInput}
              value={hostDetails[key]}
              onChange={(e) => setHostField(key, e.target.value)}
              placeholder={label}
              data-testid={`host-field-${key}`}
            />
          </div>
        ))}
      </div>

      {/* Save Changes Button */}
      <button
        style={styles.primaryButton}
        onClick={handleSave}
        data-testid="save-identity-btn"
      >
        <span style={styles.primaryButtonText}>Save Changes</span>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 3: NOTIFICATIONS TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function NotificationsTab() {
  const [toggles, setToggles] = useState(NOTIFY_DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  // Load notification preferences from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('admin.settings.notifications');
    if (stored) {
      try {
        setToggles({ ...NOTIFY_DEFAULTS, ...JSON.parse(stored) });
      } catch (e) {
        // Ignore parse errors
      }
    }
    setLoaded(true);
  }, []);

  // Toggle handler - updates state and persists to localStorage
  const toggle = (key) => {
    const next = { ...toggles, [key]: !toggles[key] };
    setToggles(next);
    localStorage.setItem('admin.settings.notifications', JSON.stringify(next));
  };

  // Don't render until loaded to prevent flash
  if (!loaded) return null;

  return (
    <div style={styles.listStack} data-testid="notifications-tab">
      {Object.keys(NOTIFY_LABELS).map((key) => (
        <div
          key={key}
          style={styles.notifyRow}
          onClick={() => toggle(key)}
          role="button"
          tabIndex={0}
          data-testid={`notify-row-${key}`}
        >
          <span style={styles.notifyLabel}>{NOTIFY_LABELS[key]}</span>
          <div
            style={{
              ...styles.toggleTrack,
              ...(toggles[key] ? styles.toggleTrackActive : {}),
            }}
          >
            <div
              style={{
                ...styles.toggleThumb,
                ...(toggles[key] ? styles.toggleThumbActive : {}),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 4: THEME SYSTEM TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function ThemeTab() {
  const [selectedTheme, setSelectedTheme] = useState('luxury');
  const [loaded, setLoaded] = useState(false);

  // Load theme selection from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('admin.settings.theme');
    if (stored) {
      setSelectedTheme(stored);
    }
    setLoaded(true);
  }, []);

  // Theme selection handler
  const selectTheme = (themeId) => {
    const theme = THEMES.find(t => t.id === themeId);
    if (theme && theme.available) {
      setSelectedTheme(themeId);
      localStorage.setItem('admin.settings.theme', themeId);
    }
  };

  // Get icon component for theme
  const getThemeIcon = (iconName) => {
    switch (iconName) {
      case 'sparkles':
        return <Sparkles size={20} color={COLORS.white} />;
      case 'palette':
        return <Palette size={20} color={COLORS.white} />;
      default:
        return <Lock size={16} color={COLORS.grey} />;
    }
  };

  if (!loaded) return null;

  return (
    <div data-testid="theme-tab">
      <p style={styles.panelIntro}>
        Select a visual theme for your event dashboard.
      </p>
      <div style={styles.themeGrid}>
        {THEMES.map((theme) => {
          const isActive = selectedTheme === theme.id;
          const isDisabled = !theme.available;

          return (
            <div
              key={theme.id}
              style={{
                ...styles.themeCard,
                ...(isActive && !isDisabled ? styles.themeCardActive : {}),
                ...(isDisabled ? styles.themeCardDisabled : {}),
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
              onClick={() => !isDisabled && selectTheme(theme.id)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              data-testid={`theme-card-${theme.id}`}
            >
              {/* Active Badge */}
              {isActive && !isDisabled && (
                <div style={styles.activeBadge}>
                  <span style={styles.activeBadgeText}>Active</span>
                </div>
              )}

              {/* Coming Soon Badge for disabled themes */}
              {isDisabled && (
                <div style={styles.comingSoonBadge}>
                  <span style={styles.comingSoonBadgeText}>Soon</span>
                </div>
              )}

              {/* Theme Swatch */}
              <div
                style={{
                  ...styles.themeSwatch,
                  backgroundColor: theme.swatch,
                }}
              >
                {getThemeIcon(theme.icon)}
              </div>

              {/* Theme Name */}
              <span style={styles.themeName}>{theme.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 5: ROLES TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function RolesTab() {
  return (
    <div data-testid="roles-tab">
      <div style={styles.listStack}>
        {/* Role Rows */}
        {ROLES.map((role) => (
          <div key={role.initial} style={styles.roleRow} data-testid={`role-row-${role.initial.toLowerCase()}`}>
            {/* Role Avatar */}
            <div style={styles.roleAvatar}>
              <span style={styles.roleAvatarText}>{role.initial}</span>
            </div>

            {/* Role Content */}
            <div style={styles.roleContent}>
              <p style={styles.roleName}>{role.name}</p>
              <p style={styles.roleAccess}>{role.desc}</p>
            </div>

            {/* Role Badge */}
            <div style={styles.roleBadge}>
              <span style={styles.roleBadgeText}>{role.access}</span>
            </div>
          </div>
        ))}

        {/* Future Card: Permissions Matrix */}
        <div style={styles.futureCard} data-testid="future-card-permissions">
          <div style={styles.futureCardIconWrap}>
            <Grid3X3 size={18} color={COLORS.grey} />
          </div>
          <div style={styles.futureCardContent}>
            <p style={styles.futureCardTitle}>Permissions Matrix</p>
            <p style={styles.futureCardDesc}>Fine-grained control over feature access for each role</p>
          </div>
          <div style={styles.comingSoonBadge}>
            <span style={styles.comingSoonBadgeText}>Soon</span>
          </div>
        </div>

        {/* Future Card: Invite Collaborator */}
        <div style={styles.futureCard} data-testid="future-card-invite">
          <div style={styles.futureCardIconWrap}>
            <UserPlus size={18} color={COLORS.grey} />
          </div>
          <div style={styles.futureCardContent}>
            <p style={styles.futureCardTitle}>Invite Collaborator</p>
            <p style={styles.futureCardDesc}>Add team members with specific roles and permissions</p>
          </div>
          <div style={styles.comingSoonBadge}>
            <span style={styles.comingSoonBadgeText}>Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 6: INTEGRATIONS TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function IntegrationsTab() {
  return (
    <div data-testid="integrations-tab">
      <div style={styles.listStack}>
        {INTEGRATIONS.map((integration) => {
          const isActive = integration.status === 'Active';
          const initial = integration.name.charAt(0).toUpperCase();

          return (
            <div 
              key={integration.id} 
              style={styles.integrationRow} 
              data-testid={`integration-row-${integration.id}`}
            >
              {/* Integration Initial */}
              <div style={styles.integrationInitial}>
                <span style={styles.integrationInitialText}>{initial}</span>
              </div>

              {/* Integration Content */}
              <div style={styles.integrationContent}>
                <p style={styles.integrationName}>{integration.name}</p>
                <p style={styles.integrationDesc}>{integration.desc}</p>
              </div>

              {/* Status Badge */}
              <div style={{
                ...styles.integrationBadge,
                ...(isActive ? styles.integrationBadgeOn : styles.integrationBadgeOff),
              }}>
                <span style={{
                  ...styles.integrationBadgeText,
                  ...(isActive ? styles.integrationBadgeTextOn : {}),
                }}>
                  {integration.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 7: ADMIN TOOLS TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function AdminToolsTab() {
  const handleOpenTrash = () => {
    alert('Trash & Restore functionality will be available when backend integration is complete. This will allow you to view and restore deleted guests, vendors, and messages.');
  };

  return (
    <div data-testid="admin-tools-tab">
      <div style={styles.listStack}>
        {/* Trash & Restore Card */}
        <div style={styles.adminTrashCard} data-testid="admin-trash-card">
          <div style={styles.cardHeaderRow}>
            <div style={styles.cardHeaderIconWrap}>
              <Trash2 size={18} color={COLORS.gold} />
            </div>
            <h3 style={styles.cardHeaderTitle}>Trash & Restore</h3>
          </div>
          <p style={styles.cardBodyText}>
            View and restore recently deleted items including guests, vendors, and messages. Items are permanently removed after 30 days.
          </p>
          <button
            style={styles.secondaryButton}
            onClick={handleOpenTrash}
            data-testid="open-trash-btn"
          >
            <span style={styles.secondaryButtonText}>Open Trash</span>
          </button>
        </div>

        {/* Future Card: Data Export & Backup */}
        <div style={styles.futureCard} data-testid="future-card-export">
          <div style={styles.futureCardIconWrap}>
            <Download size={18} color={COLORS.grey} />
          </div>
          <div style={styles.futureCardContent}>
            <p style={styles.futureCardTitle}>Data Export & Backup</p>
            <p style={styles.futureCardDesc}>Export all event data or create scheduled backups for safekeeping</p>
          </div>
          <div style={styles.comingSoonBadge}>
            <span style={styles.comingSoonBadgeText}>Soon</span>
          </div>
        </div>

        {/* Future Card: Activity Audit Log */}
        <div style={styles.futureCard} data-testid="future-card-audit">
          <div style={styles.futureCardIconWrap}>
            <FileText size={18} color={COLORS.grey} />
          </div>
          <div style={styles.futureCardContent}>
            <p style={styles.futureCardTitle}>Activity Audit Log</p>
            <p style={styles.futureCardDesc}>Track all changes and actions taken by team members</p>
          </div>
          <div style={styles.comingSoonBadge}>
            <span style={styles.comingSoonBadgeText}>Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GUEST-FACING CONTENT TAB
// Single guest_site_config document — manages all guest-page content.
// Sponsors stay in Finance. This tab: travel, discover cards, rsvp display.
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_CONFIG = {
  travel: {
    hotels: { enabled: false, room_block_enabled: false,
      primary_hotel: { name: '', address: '', booking_url: '', discount_code: '' },
      additional_hotels: [] },
    flights: { enabled: false, preferred_airport: 'EWR', tips: '' }
  },
  discover_cards: { enabled: true,
    travel:       { subtitle: 'Getting to our celebration' },
    things_to_do: { subtitle: 'Explore the area' },
    calendar:     { subtitle: "See Carol's Schedule" }
  },
  rsvp_display: { enabled: true, deadline_label: 'Kindly RSVP by' }
};

function Toggle({ checked, onChange, label, description, testId }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:16 }}>
      <div>
        <p style={{ fontSize:13, fontWeight:700, color:'#2A2218', margin:0 }}>{label}</p>
        {description && <p style={{ fontSize:11, color:'#7A6A40', margin:'3px 0 0' }}>{description}</p>}
      </div>
      <button onClick={() => onChange(!checked)} data-testid={testId}
        style={{ width:44, height:24, borderRadius:12, border:'none', cursor:'pointer', flexShrink:0,
          background: checked ? '#C6A75A' : 'rgba(198,167,90,0.25)', position:'relative', transition:'background 0.2s' }}>
        <div style={{ position:'absolute', top:3, left: checked ? 23 : 3, width:18, height:18,
          borderRadius:'50%', background:'#fff', transition:'left 0.2s',
          boxShadow:'0 1px 4px rgba(0,0,0,0.15)' }} />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', testId }) {
  const s = { fontSize:12, fontWeight:600, color:'#5A4E38', marginBottom:5, display:'block' };
  const inp = { width:'100%', padding:'9px 12px', borderRadius:9, boxSizing:'border-box',
    border:'1.5px solid rgba(198,167,90,0.30)', background:'rgba(255,253,246,0.80)',
    fontSize:13, color:'#2A2218', fontFamily:'inherit', outline:'none' };
  return (
    <div style={{ marginBottom:12 }}>
      <label style={s}>{label}</label>
      {type === 'textarea'
        ? <textarea value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} rows={3}
            style={{ ...inp, resize:'vertical', lineHeight:1.5 }} data-testid={testId} />
        : <input value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} type={type}
            style={inp} data-testid={testId} />}
    </div>
  );
}

function SectionCard({ title, children, accent = '#C6A75A' }) {
  return (
    <div style={{ borderRadius:14, border:'1.5px solid rgba(198,167,90,0.22)',
      background:'rgba(255,253,246,0.60)', marginBottom:20, overflow:'hidden' }}>
      <div style={{ padding:'12px 18px', background:'rgba(198,167,90,0.08)',
        borderBottom:'1px solid rgba(198,167,90,0.18)' }}>
        <p style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'1.2px',
          color:'#9B7420', margin:0 }}>{title}</p>
      </div>
      <div style={{ padding:'18px' }}>{children}</div>
    </div>
  );
}

function GuestSiteTab() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [config, setConfig] = React.useState(DEFAULT_CONFIG);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [videoUploading, setVideoUploading] = React.useState(false);
  const [videoStatus, setVideoStatus] = React.useState('');
  const videoInputRef = React.useRef(null);

  React.useEffect(() => {
    fetch(`${API}/api/guest-site-config`)
      .then(r => r.json())
      .then(d => {
        setConfig(prev => ({ ...prev, ...d }));
        if (d?.video_url) setVideoStatus('Video uploaded ✓');
      })
      .catch(() => {});
  }, []);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoUploading(true);
    setVideoStatus('Uploading…');
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${API}/api/admin/upload-video`, { method: 'POST', body: form });
      const data = await res.json();
      if (res.ok) {
        setVideoStatus(`Uploaded: ${file.name} ✓`);
        setConfig(prev => ({ ...prev, video_url: data.video_url }));
      } else {
        setVideoStatus(`Error: ${data.detail || 'Upload failed'}`);
      }
    } catch {
      setVideoStatus('Upload failed — check connection.');
    } finally {
      setVideoUploading(false);
    }
  };

  const update = (path, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const addHotel = () => {
    update('travel.hotels.additional_hotels', [
      ...(config.travel?.hotels?.additional_hotels || []),
      { name: '', address: '', maps_url: '' }
    ]);
  };

  const updateHotel = (i, field, value) => {
    const hotels = [...(config.travel?.hotels?.additional_hotels || [])];
    hotels[i] = { ...hotels[i], [field]: value };
    update('travel.hotels.additional_hotels', hotels);
  };

  const removeHotel = (i) => {
    update('travel.hotels.additional_hotels',
      (config.travel?.hotels?.additional_hotels || []).filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/guest-site-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setSaving(false);
  };

  const h = config.travel?.hotels || {};
  const ph = h.primary_hotel || {};
  const fl = config.travel?.flights || {};
  const dc = config.discover_cards || {};
  const rd = config.rsvp_display || {};

  return (
    <div>
      <p style={{ fontSize:12, color:'#7A6A40', marginBottom:20, lineHeight:1.6 }}>
        Manage all content shown on the guest-facing site. Sponsors are managed in Finance.
        Changes save immediately and appear on the guest page on next load.
      </p>

      {/* ── INVITE FILM ── */}
      <SectionCard title="Invite Film (Experience Page)">
        <p style={{ fontSize:12, color:'#7A6A40', marginBottom:14, lineHeight:1.6 }}>
          Upload the MP4 file that plays on the <strong>/experience/:token</strong> invite page.
          The file is served directly from the server — no YouTube or Vimeo required.
          Max recommended size: 500MB. Accepted: .mp4, .mov, .webm
        </p>
        <input
          ref={videoInputRef}
          type="file"
          accept=".mp4,.mov,.webm"
          style={{ display:'none' }}
          onChange={handleVideoUpload}
        />
        <button
          onClick={() => videoInputRef.current?.click()}
          disabled={videoUploading}
          data-testid="upload-invite-video-btn"
          style={{
            padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer',
            background:'linear-gradient(145deg,#D4AA50,#C49830)',
            boxShadow:'0 4px 12px rgba(180,130,20,0.28)',
            fontSize:12, fontWeight:700, color:'#FFF5D0', letterSpacing:'1px',
            opacity: videoUploading ? 0.6 : 1,
          }}
        >
          {videoUploading ? 'Uploading…' : 'Choose Video File'}
        </button>
        {videoStatus && (
          <p style={{ fontSize:11, color: videoStatus.includes('✓') ? '#2D7A4F' : '#9B3030',
            marginTop:10, fontStyle:'italic' }}>
            {videoStatus}
          </p>
        )}
        {config.video_url && (
          <p style={{ fontSize:10, color:'#7A6A40', marginTop:8, wordBreak:'break-all' }}>
            Stored path: <code style={{ background:'rgba(0,0,0,0.06)', padding:'1px 4px', borderRadius:4 }}>
              {config.video_url}
            </code>
          </p>
        )}
      </SectionCard>

      {/* ── TRAVEL: HOTELS ── */}
      <SectionCard title="Travel — Hotels">
        <Toggle label="Hotels section enabled" checked={!!h.enabled}
          onChange={v => update('travel.hotels.enabled', v)}
          description="Show the Hotels tile in the Discover carousel"
          testId="hotels-enabled-toggle" />
        {h.enabled && <>
          <Toggle label="Room Block Available" checked={!!h.room_block_enabled}
            onChange={v => update('travel.hotels.room_block_enabled', v)}
            description="Show the host-recommended hotel with direct booking" />
          {h.room_block_enabled && <>
            <Field label="Hotel Name" value={ph.name || ''} onChange={v => update('travel.hotels.primary_hotel.name', v)}
              placeholder="e.g. Sheraton Mahwah" testId="hotel-name" />
            <Field label="Hotel Address" value={ph.address || ''} onChange={v => update('travel.hotels.primary_hotel.address', v)}
              placeholder="1 International Blvd, Mahwah, NJ" testId="hotel-address" />
            <Field label="Booking URL" value={ph.booking_url || ''} onChange={v => update('travel.hotels.primary_hotel.booking_url', v)}
              placeholder="https://marriott.com/...?code=CV80" testId="hotel-booking-url" />
            <Field label="Discount Code" value={ph.discount_code || ''} onChange={v => update('travel.hotels.primary_hotel.discount_code', v)}
              placeholder="e.g. CV80" testId="hotel-discount-code" />
          </>}
          <div style={{ marginTop:8 }}>
            <p style={{ fontSize:12, fontWeight:700, color:'#5A4E38', marginBottom:8 }}>
              Additional Nearby Hotels
            </p>
            {(h.additional_hotels || []).map((hotel, i) => (
              <div key={i} style={{ border:'1px solid rgba(198,167,90,0.22)', borderRadius:10,
                padding:'12px', marginBottom:10, background:'rgba(255,253,246,0.40)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#9B7420' }}>Hotel {i + 1}</span>
                  <button onClick={() => removeHotel(i)} style={{ fontSize:10, color:'#9B3030',
                    background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>Remove</button>
                </div>
                <Field label="Hotel Name" value={hotel.name || ''} onChange={v => updateHotel(i,'name',v)} placeholder="Hotel name" />
                <Field label="Address / Maps URL" value={hotel.address || ''} onChange={v => updateHotel(i,'address',v)} placeholder="Address or Google Maps link" />
              </div>
            ))}
            <button onClick={addHotel} style={{ padding:'9px 16px', borderRadius:9, cursor:'pointer',
              background:'rgba(198,167,90,0.10)', border:'1.5px dashed rgba(198,167,90,0.40)',
              fontSize:12, fontWeight:600, color:'#9B7420' }}>
              + Add Hotel
            </button>
          </div>
        </>}
      </SectionCard>

      {/* ── TRAVEL: FLIGHTS ── */}
      <SectionCard title="Travel — Flights">
        <Toggle label="Flights section enabled" checked={!!fl.enabled}
          onChange={v => update('travel.flights.enabled', v)}
          description="Show the Flights tile in the Discover carousel"
          testId="flights-enabled-toggle" />
        {fl.enabled && <>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#5A4E38', marginBottom:8, display:'block' }}>
              Highlighted Airport
            </label>
            <div style={{ display:'flex', gap:8 }}>
              {['EWR','JFK','LGA'].map(apt => (
                <button key={apt} onClick={() => update('travel.flights.preferred_airport', apt)}
                  style={{ padding:'8px 16px', borderRadius:9, cursor:'pointer', fontSize:12, fontWeight:700,
                    border: fl.preferred_airport === apt ? '2px solid #C6A75A' : '1.5px solid rgba(198,167,90,0.28)',
                    background: fl.preferred_airport === apt ? 'rgba(198,167,90,0.14)' : 'rgba(255,253,246,0.80)',
                    color: fl.preferred_airport === apt ? '#9B7420' : '#7A6A40' }}>
                  {apt}
                </button>
              ))}
            </div>
          </div>
          <Field label="Travel Tips" value={fl.tips || ''} onChange={v => update('travel.flights.tips', v)}
            type="textarea" placeholder="e.g. EWR is closest to the venue (8 miles). Fly in Friday for best weekend rates."
            testId="flight-tips" />
        </>}
      </SectionCard>

      {/* ── DISCOVER CARDS ── */}
      <SectionCard title="Discover Cards — Subtitles">
        <p style={{ fontSize:11, color:'#7A6A40', marginBottom:14 }}>
          Edit the subtitle shown beneath each card label in the Discover carousel.
        </p>
        <Field label="Travel card subtitle" value={dc.travel?.subtitle || ''}
          onChange={v => update('discover_cards.travel.subtitle', v)}
          placeholder="Getting to our celebration" testId="discover-travel-subtitle" />
        <Field label="Things to Do card subtitle" value={dc.things_to_do?.subtitle || ''}
          onChange={v => update('discover_cards.things_to_do.subtitle', v)}
          placeholder="Explore the area" testId="discover-things-subtitle" />
        <Field label="Calendar card subtitle" value={dc.calendar?.subtitle || ''}
          onChange={v => update('discover_cards.calendar.subtitle', v)}
          placeholder="See Carol's Schedule" testId="discover-calendar-subtitle" />
      </SectionCard>

      {/* ── EVENT DETAILS ── */}
      <SectionCard title="Event Details — The Evening Card">
        <p style={{ fontSize:11, color:'#7A6A40', marginBottom:14, lineHeight:1.5 }}>
          These fields power the "The Evening" section on the guest page — date, time, venue, and attire.
          All fields are admin-controlled and update the guest page immediately on save.
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <Field label="Day" value={config.event_details?.day || ''}
            onChange={v => update('event_details.day', v)} placeholder="Saturday" testId="event-day" />
          <Field label="Time" value={config.event_details?.time || ''}
            onChange={v => update('event_details.time', v)} placeholder="12:30 PM" testId="event-time" />
        </div>
        <Field label="Full Date" value={config.event_details?.date || ''}
          onChange={v => update('event_details.date', v)} placeholder="July 26, 2026" testId="event-date" />
        <Field label="Venue Name" value={config.event_details?.venue_name || ''}
          onChange={v => update('event_details.venue_name', v)}
          placeholder="Season's Catering & Events" testId="event-venue-name" />
        <Field label="Venue Address (tap-for-directions)" value={config.event_details?.venue_address || ''}
          onChange={v => update('event_details.venue_address', v)}
          placeholder="570 US-46, Township of Washington, NJ 07676" testId="event-venue-address" />
        <Field label="Venue Photo URL (optional)" value={config.event_details?.venue_photo_url || ''}
          onChange={v => update('event_details.venue_photo_url', v)}
          placeholder="https://..." testId="event-venue-photo" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="Attire — Men" value={config.event_details?.attire_note_men || ''}
            onChange={v => update('event_details.attire_note_men', v)}
            placeholder="Suit & Tie" testId="attire-men" />
          <Field label="Attire — Women" value={config.event_details?.attire_note_women || ''}
            onChange={v => update('event_details.attire_note_women', v)}
            placeholder="Cocktail or Floor-Length Gown" testId="attire-women" />
        </div>
      </SectionCard>

      {/* ── THINGS TO DO ── */}
      <SectionCard title="Things to Do — NJ Guide">
        <Toggle label="NJ Guide section enabled" checked={!!config.things_to_do?.enabled}
          onChange={v => update('things_to_do.enabled', v)}
          description="Show the NJ Guide tile in the Discover carousel"
          testId="things-enabled-toggle" />
        {config.things_to_do?.enabled && (
          <div style={{ marginTop:8 }}>
            <p style={{ fontSize:12, fontWeight:700, color:'#5A4E38', marginBottom:4 }}>
              Curated Places
            </p>
            <p style={{ fontSize:11, color:'#7A6A40', marginBottom:12, lineHeight:1.5 }}>
              Add 10–15 hand-picked places. These feed the category filters and discovery feed guests see.
            </p>
            {(config.things_to_do?.places || []).map((place, i) => (
              <div key={i} style={{ border:'1px solid rgba(198,167,90,0.22)', borderRadius:10,
                padding:'14px', marginBottom:12, background:'rgba(255,253,246,0.40)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#9B7420' }}>
                    Place {i + 1}{place.is_host_favorite ? ' ★ Carol\'s Choice' : ''}
                  </span>
                  <button onClick={() => {
                    const places = [...(config.things_to_do?.places || [])];
                    places.splice(i, 1);
                    update('things_to_do.places', places);
                  }} style={{ fontSize:10, color:'#9B3030', background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>
                    Remove
                  </button>
                </div>
                {[['name','Name','e.g. American Dream Mall'],
                  ['address','Address','Full address for directions'],
                  ['photo_url','Photo URL (optional)','https://...'],
                  ['host_note','Host Note (optional)',"Mom loves the ferris wheel for the sunset views!"]
                ].map(([key, label, ph]) => (
                  <Field key={key} label={label} value={place[key] || ''}
                    onChange={v => {
                      const places = JSON.parse(JSON.stringify(config.things_to_do?.places || []));
                      places[i][key] = v;
                      update('things_to_do.places', places);
                    }}
                    placeholder={ph}
                    type={key === 'host_note' ? 'textarea' : 'text'} />
                ))}
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#5A4E38', marginBottom:6, display:'block' }}>Category</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {[['venue_hotels','Venue & Hotels'],['moms_favorites',"Mom's Favorites"],
                      ['food_dining','Food & Dining'],['activities','Activities'],['must_visits','Must-Visits']
                    ].map(([val, lbl]) => (
                      <button key={val}
                        onClick={() => {
                          const places = JSON.parse(JSON.stringify(config.things_to_do?.places || []));
                          places[i].category = val;
                          update('things_to_do.places', places);
                        }}
                        style={{ padding:'6px 12px', borderRadius:8, cursor:'pointer', fontSize:11, fontWeight:600,
                          border: place.category === val ? '2px solid #C6A75A' : '1.5px solid rgba(198,167,90,0.28)',
                          background: place.category === val ? 'rgba(198,167,90,0.14)' : 'rgba(255,253,246,0.80)',
                          color: place.category === val ? '#9B7420' : '#7A6A40' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <Toggle label="Carol's Choice (Mom's Favorite)"
                  checked={!!place.is_host_favorite}
                  onChange={v => {
                    const places = JSON.parse(JSON.stringify(config.things_to_do?.places || []));
                    places[i].is_host_favorite = v;
                    update('things_to_do.places', places);
                  }}
                  description="Shows a special crown icon on this place" />
              </div>
            ))}
            <button onClick={() => update('things_to_do.places', [
              ...(config.things_to_do?.places || []),
              { name:'', category:'activities', address:'', photo_url:'', host_note:'', is_host_favorite:false }
            ])} style={{ padding:'9px 16px', borderRadius:9, cursor:'pointer',
              background:'rgba(198,167,90,0.10)', border:'1.5px dashed rgba(198,167,90,0.40)',
              fontSize:12, fontWeight:600, color:'#9B7420' }}>
              + Add Place
            </button>
          </div>
        )}
      </SectionCard>

      {/* ── RSVP DISPLAY ── */}
      <SectionCard title="RSVP Display">
        <Field label="RSVP deadline label" value={rd.deadline_label || ''}
          onChange={v => update('rsvp_display.deadline_label', v)}
          placeholder="Kindly RSVP by" testId="rsvp-deadline-label" />
        <Field label="RSVP deadline date (YYYY-MM-DD)" value={rd.rsvp_deadline || ''}
          onChange={v => update('rsvp_display.rsvp_deadline', v)}
          placeholder="2026-06-15" testId="rsvp-deadline-date" />
        <Field label="Event date (YYYY-MM-DD)" value={rd.event_date || ''}
          onChange={v => update('rsvp_display.event_date', v)}
          placeholder="2026-07-26" testId="rsvp-event-date" />
      </SectionCard>

      {/* Save */}
      <button onClick={save} disabled={saving} data-testid="save-guest-site-config"
        style={{ width:'100%', height:48, borderRadius:12, border:'none', cursor:'pointer',
          background: saved ? 'rgba(34,160,90,0.85)' : 'linear-gradient(145deg,#D4AA50,#C49830)',
          fontSize:13, fontWeight:700, color:'#FFF5D0', letterSpacing:'1px',
          textTransform:'uppercase', transition:'background 0.3s',
          boxShadow:'0 4px 14px rgba(180,130,20,0.30)' }}>
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Guest Site Config'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function SettingsTab({ eventConfig = {}, onSaveIdentity = () => {} }) {
  const [activeTab, setActiveTab] = useState('identity');

  return (
    <div style={styles.tabContent} data-testid="settings-panel">
      {/* ═══════════════════════════════════════════════════════════════════════
          PART 1: Panel Header
          ═══════════════════════════════════════════════════════════════════════ */}
      <AdminPageHeader
        eyebrow="Configuration"
        title="Settings"
        tagline="Event configuration, preferences, and integrations"
      />

      {/* ═══════════════════════════════════════════════════════════════════════
          PART 1: Tab Navigation
          ═══════════════════════════════════════════════════════════════════════ */}
      <div style={styles.tabNavContainer} data-testid="settings-tab-nav">
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              data-testid={`settings-tab-${tab.key}`}
              style={{
                ...styles.glFilterChip,
                ...(isActive ? styles.glFilterChipActive : {}),
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              <span
                style={{
                  ...styles.glFilterChipText,
                  ...(isActive ? styles.glFilterChipTextActive : {}),
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PART 2: Event Identity Tab Content
          ═══════════════════════════════════════════════════════════════════════ */}
      <TabShell>
        {activeTab === 'identity' && (
          <IdentityTab eventConfig={eventConfig} onSaveIdentity={onSaveIdentity} />
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 3: Notifications Tab Content
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'guest-site' && (
          <GuestExperienceEditor />
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab />
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 4: Theme System Tab Content
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'theme' && (
          <ThemeTab />
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 5: Role Access Tab Content
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'roles' && (
          <RolesTab />
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 6: Integrations Tab Content
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'integrations' && (
          <IntegrationsTab />
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 7: Admin Tools Tab Content
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'admin' && (
          <AdminToolsTab />
        )}
      </TabShell>
    </div>
  );
}
